
import React from 'react';
import TextInputField from '../review-form/TextInputField';
import TextareaField from '../review-form/TextareaField';
import DatePickerField from '../review-form/DatePickerField';
import RadioGroupField from '../review-form/RadioGroupField';
import JobCategoryField from '../review-form/JobCategoryField';
import TenureField from '../review-form/TenureField';
import SalaryField from '../review-form/SalaryField'; // Import the new SalaryField
import CompanyPerksRating from '../review-form/CompanyPerksRating';
import { useFetchCompanyPerks } from '@/hooks/useFetchCompanyPerks';
import { ReviewFormData } from '../review-form/useReviewForm';
import { PerkRating } from '@/lib/types';
import { australianLocations } from '@/components/company/CompanyFormSchema';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface ReviewFormFieldsProps {
  formData: ReviewFormData;
  charCounts: {
    reasonForExit: number;
    biggestProblem: number;
  };
  reasonForExitMaxChars: number;
  biggestProblemMaxChars: number;
  onTextChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onDateChange: (date: Date | undefined) => void;
  onRadioChange: (field: string, value: boolean) => void;
  onCategoryChange: (value: string) => void;
  onTenureChange: (value: string) => void;
  onPerkRatingsChange: (perkRatings: PerkRating[]) => void;
  onLocationChange: (value: string) => void;
  onSalaryChange: (value: number | undefined) => void; // Add salary change handler
  initialCompany?: string;
}

const ReviewFormFields = ({
  formData,
  charCounts,
  reasonForExitMaxChars,
  biggestProblemMaxChars,
  onTextChange,
  onDateChange,
  onRadioChange,
  onCategoryChange,
  onTenureChange,
  onPerkRatingsChange,
  onLocationChange,
  onSalaryChange, // Add salary change handler
  initialCompany
}: ReviewFormFieldsProps) => {
  // Fetch company perks if a company is specified
  const { perks, isLoading: perksLoading } = useFetchCompanyPerks(formData.company);

  return (
    <>
      <TextInputField
        id="company"
        name="company"
        label="Company"
        placeholder="Where did you work?"
        value={formData.company}
        onChange={onTextChange}
        readOnly={!!initialCompany}
        hideCounter={true}
      />
      
      <TextInputField
        id="jobTitle"
        name="jobTitle"
        label="Job Title"
        placeholder="What was your position?"
        value={formData.jobTitle}
        onChange={onTextChange}
        hideCounter={true}
      />
      
      <JobCategoryField
        id="jobCategory"
        label="Job Category"
        value={formData.jobCategory || ''}
        onChange={onCategoryChange}
      />
      
      {/* Add the Salary field */}
      <SalaryField
        id="salary"
        label="Annual Salary"
        value={formData.salary}
        onChange={onSalaryChange}
      />
      
      <TextareaField
        id="reasonForExit"
        name="reasonForExit"
        label="Reason for Exit"
        placeholder="Why did you leave? What were the red flags?"
        value={formData.reasonForExit}
        charCount={charCounts.reasonForExit}
        maxChars={reasonForExitMaxChars}
        onChange={onTextChange}
      />
      
      <TextareaField
        id="biggestProblem"
        name="biggestProblem"
        label="Biggest problem at this company"
        placeholder="What was the most significant issue you experienced?"
        value={formData.biggestProblem || ''}
        charCount={charCounts.biggestProblem}
        maxChars={biggestProblemMaxChars}
        onChange={onTextChange}
      />
      
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Select
          value={formData.location}
          onValueChange={onLocationChange}
        >
          <SelectTrigger id="location" className="w-full">
            <SelectValue placeholder="Select a location" />
          </SelectTrigger>
          <SelectContent>
            {australianLocations.map((location) => (
              <SelectItem key={location} value={location}>
                {location}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TenureField
        id="tenure"
        label="How long did you work here?"
        value={formData.tenure}
        onChange={onTenureChange}
      />

      <DatePickerField
        id="exitDate"
        label="Exit Date"
        date={formData.exitDate}
        onSelect={onDateChange}
      />

      <RadioGroupField
        id="fairWorkInvolved"
        label="Was Fair Work involved?"
        value={formData.fairWorkInvolved}
        onChange={(value) => onRadioChange('fairWorkInvolved', value)}
      />
      
      <RadioGroupField
        id="positiveExperience"
        label="Was your exit a positive experience?"
        value={formData.positiveExperience}
        onChange={(value) => onRadioChange('positiveExperience', value)}
      />

      {/* Company perks rating */}
      {!perksLoading && perks && (
        <CompanyPerksRating
          companyPerks={perks}
          perkRatings={formData.perkRatings || []}
          onChange={onPerkRatingsChange}
        />
      )}
    </>
  );
};

export default ReviewFormFields;
